import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

import { HousingRoomBudgetFilters } from './HousingRoomBudgetFilters';
import type { ExtendedAdsQueryVariables } from '../types/extendedAdsQueryVariables';
import Typography from 'src/components/ui/Typography';
import { styledComponent, styledTag } from 'src/utils/styled';
import TextField from 'src/components/ui/TextField';
import { colours } from 'src/constants/colours';
import Checkbox from 'src/components/ui/Checkbox';
import type { FormValues } from '../types/adSearchFormValues';
import useGetRoomsNumberItems from 'src/hooks/useGetRoomsNumberItems';
import { additionalAssets, principalAssets } from 'src/constants/assetTypes';
import { useWindowSize } from '../hooks/useWindowSize';

const MinMaxInputs = styledComponent(Box)<{
    $isAreas?: boolean;
}>`
    .MuiFormControl-root {
        margin: 0 0.5rem;
        ${({ $isAreas }) => $isAreas && `width: 5rem;`}
    }
    .MuiInputBase-root {
        height: 2.5rem;
    }
    .MuiInputBase-input {
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
`;

const StyledToggleButtonGroup = styledComponent(ToggleButtonGroup)`
    margin-left: 1rem;
    .MuiToggleButtonGroup-grouped {
        border-color: ${colours.primary.O72};
        margin-left: 0.5rem;
        height: 2.5rem;
        border-radius: 0.25rem;
        color: ${colours.primary.O60};
    }
`;

const StyledCheckbox = styledTag('div')<{
    $isSmallFont?: boolean;
}>`
    label {
        color: ${colours.primary.O72};
        ${({ $isSmallFont }) =>
            $isSmallFont &&
            `.MuiCheckbox-root {
                padding-right: 0.25rem;
            }
            .MuiTypography-root {
                font-size: 0.85rem;
            }`}
    }
`;

type Props = {
    open: boolean;
    values: FormValues;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    queryVariables?: ExtendedAdsQueryVariables;
    showHousingRoomBudgetFilters: boolean;
    isHybridView: boolean;
};

export const AdSearchFormFilters: React.FC<Props> = ({
    open,
    values,
    setFieldValue,
    queryVariables,
    showHousingRoomBudgetFilters,
    isHybridView,
}) => {
    const { t } = useTranslation(['ad']);
    const { isDesktopSmall, isDesktopLarge, isTabletLandscape, isMobile } = useWindowSize();

    const notHybridViewAndLargeScreen = useMemo(
        () => !isHybridView && (isDesktopSmall || isDesktopLarge || isTabletLandscape),
        [isDesktopLarge, isDesktopSmall, isHybridView, isTabletLandscape],
    );
    const smallFontForCheckBoxes = useMemo(
        () => (isHybridView && isDesktopSmall) || isMobile,
        [isDesktopSmall, isHybridView, isMobile],
    );

    const bedroomNumberItems = useGetRoomsNumberItems('bedRooms');

    const handleAssetsToggle = (value: string) => () => {
        if (values.assets) {
            const currentIndex = values.assets.indexOf(value);
            const newChecked = [...values.assets];

            if (currentIndex === -1) {
                newChecked.push(value);
            } else {
                newChecked.splice(currentIndex, 1);
            }
            setFieldValue('assets', newChecked.length > 0 ? newChecked : undefined);
        } else {
            setFieldValue('assets', [value]);
        }
    };

    return (
        <Box width="100%">
            <Collapse in={open}>
                <Box display="flex" flexDirection="column">
                    <Grid spacing={1} container>
                        {showHousingRoomBudgetFilters && (
                            <HousingRoomBudgetFilters searchType={values.type} queryVariables={queryVariables} xs={4} />
                        )}
                        <Grid xs={12} sm={6} lg={isHybridView ? 12 : 6} xl={isHybridView ? 6 : 6} item>
                            <Box display="flex" flexDirection="row" alignItems="center" flex="1" height="100%">
                                <Typography variant="h5" style={{ fontWeight: 500 }}>
                                    {t('ad:area')}
                                </Typography>
                                <MinMaxInputs display="flex" flexDirection="row" width="fit-content" $isAreas>
                                    <TextField
                                        InputProps={{
                                            inputProps: {
                                                'data-target': 'inp-min',
                                                'data-target-id': 'inp-min',
                                            },
                                        }}
                                        name="minLivingArea"
                                        type="number"
                                        placeholder={t('ad:min')}
                                    />
                                    <TextField
                                        InputProps={{
                                            inputProps: {
                                                'data-target': 'inp-max',
                                                'data-target-id': 'inp-max',
                                            },
                                        }}
                                        name="maxLivingArea"
                                        type="number"
                                        placeholder={t('ad:max')}
                                    />
                                </MinMaxInputs>
                            </Box>
                        </Grid>
                        <Grid xs={12} sm={6} lg={isHybridView ? 12 : 6} xl={isHybridView ? 6 : 6} item>
                            <Box display="flex" flexDirection="row" alignItems="center" flex="1" height="100%">
                                <Typography variant="h5" style={{ fontWeight: 500 }}>
                                    {t('ad:bedRooms')}
                                </Typography>
                                <StyledToggleButtonGroup
                                    value={values.bedrooms}
                                    onChange={(event: React.MouseEvent<HTMLElement>, value: number | null) =>
                                        setFieldValue('bedrooms', value ?? undefined)
                                    }
                                    aria-label="text alignment"
                                    exclusive
                                >
                                    {bedroomNumberItems.map(({ label, value }) => (
                                        <ToggleButton key={`bedRooms-${value}`} value={value}>
                                            {label}
                                        </ToggleButton>
                                    ))}
                                </StyledToggleButtonGroup>
                            </Box>
                        </Grid>
                        <Grid xs={12} md={6} lg={isHybridView ? 12 : 6} xl={isHybridView ? 12 : 6} item>
                            <Box>
                                <Typography variant="h5" style={{ fontWeight: 500 }}>
                                    {t(`ad:principalAssets`)}
                                </Typography>
                                <Box>
                                    <Grid container>
                                        {principalAssets.map((value) => (
                                            <Grid key={value} xs={notHybridViewAndLargeScreen ? 3 : 4} item>
                                                <StyledCheckbox $isSmallFont={smallFontForCheckBoxes}>
                                                    <Checkbox
                                                        name={value}
                                                        checked={values.assets?.includes(value) ?? false}
                                                        label={t(`ad:assets.${value}`)}
                                                        onChange={handleAssetsToggle(value)}
                                                    />
                                                </StyledCheckbox>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid xs={12} md={6} lg={isHybridView ? 12 : 6} xl={isHybridView ? 12 : 6} item>
                            <Box>
                                <Typography variant="h5" style={{ fontWeight: 500 }}>
                                    {t(`ad:additionalAssets`)}
                                </Typography>
                                <Box>
                                    <Grid container>
                                        {additionalAssets.map((value) => (
                                            <Grid key={value} xs={notHybridViewAndLargeScreen ? 3 : 4} item>
                                                <StyledCheckbox $isSmallFont={smallFontForCheckBoxes}>
                                                    <Checkbox
                                                        name={value}
                                                        checked={values.assets?.includes(value) ?? false}
                                                        label={t(`ad:assets.${value}`)}
                                                        onChange={handleAssetsToggle(value)}
                                                    />
                                                </StyledCheckbox>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Box>
    );
};
